/**
 * Deal banners — mobile: one card, infinite hand swipe, no autoplay
 */
class AdawatDealBanners extends HTMLElement {
  connectedCallback() {
    if (this._adawatBooted) return;
    const boot = () => this.boot();
    if (window.AdawatLazyInit?.whenVisible) {
      window.AdawatLazyInit.whenVisible(this, boot);
    } else {
      boot();
    }
  }

  boot() {
    if (this._adawatBooted) return;
    this._adawatBooted = true;

    this.viewport = this.querySelector('[data-deal-viewport]');
    if (!this.viewport) return;

    this.originals = Array.from(this.viewport.querySelectorAll('[data-deal-card]'));
    this.looping = this.originals.length > 1;
    this._skipNormalize = false;
    this.mq = window.matchMedia('(max-width: 767px)');

    if (this.looping) this.buildLoop();

    this.dragging = false;
    this.dragMoved = false;
    this.startX = 0;
    this.startScroll = 0;
    this.pointerId = null;

    this.viewport.addEventListener('scroll', () => this.normalizeLoop(), { passive: true });
    this.onResize = () => this.syncMode();
    window.addEventListener('resize', this.onResize);
    this.mq.addEventListener?.('change', this.onResize);

    this.viewport.addEventListener('pointerdown', (event) => this.onPointerDown(event));
    this.viewport.addEventListener('pointermove', (event) => this.onPointerMove(event));
    this.viewport.addEventListener('pointerup', (event) => this.onPointerUp(event));
    this.viewport.addEventListener('pointercancel', (event) => this.onPointerUp(event));
    this.viewport.addEventListener('pointerleave', (event) => {
      if (this.dragging) this.onPointerUp(event);
    });

    this.viewport.addEventListener(
      'click',
      (event) => {
        if (this.dragMoved) {
          event.preventDefault();
          event.stopPropagation();
          this.dragMoved = false;
        }
      },
      true
    );

    requestAnimationFrame(() => this.syncMode());
    this.bindSaleUpdates();
  }

  bindSaleUpdates() {
    const apply = (byVariant) => {
      this.querySelectorAll('.adawat-deal-card').forEach((card) => {
        this.applyProductSale(card, byVariant || window.__ADAWAT_SALE_BY_VARIANT__ || {});
      });
    };
    apply();
    window.addEventListener('adawat:sitewide-sale', (event) => {
      apply(event.detail?.byVariant);
    });
  }

  applyProductSale(card, byVariant) {
    const id = Number(card.getAttribute('data-variant-id') || 0);
    const baked = Number(card.getAttribute('data-adawat-sale-percent') || 0);
    const specific = id > 0 ? Number(byVariant[String(id)] || 0) : 0;
    const clamp = (n) => {
      const v = Math.round(Number(n) || 0);
      return v >= 1 && v <= 95 ? v : 0;
    };
    const percent = Math.max(clamp(baked), clamp(specific));
    if (percent <= 0) return;

    const label = `${percent}%`;
    card.setAttribute('data-adawat-sale-percent', String(percent));
    const badge = card.querySelector('[data-deal-badge-value]');
    if (badge) badge.textContent = label;
    const text = card.querySelector('.adawat-deal-card__text');
    if (text) {
      text.innerHTML = String(text.innerHTML || '').replace(/\d+\s*%/g, label);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    this.mq?.removeEventListener?.('change', this.onResize);
  }

  isMobile() {
    return this.mq ? this.mq.matches : window.innerWidth <= 767;
  }

  buildLoop() {
    if (this.viewport.querySelector('[data-deal-clone]')) return;

    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();

    this.originals.forEach((card) => {
      const prev = card.cloneNode(true);
      const next = card.cloneNode(true);
      prev.setAttribute('data-deal-clone', 'before');
      next.setAttribute('data-deal-clone', 'after');
      prev.removeAttribute('data-deal-card');
      next.removeAttribute('data-deal-card');
      prev.removeAttribute('id');
      next.removeAttribute('id');
      prev.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      next.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      before.appendChild(prev);
      after.appendChild(next);
    });

    this.viewport.insertBefore(before, this.viewport.firstChild);
    this.viewport.appendChild(after);
  }

  setWidth() {
    if (!this.originals.length) return 0;
    const first = this.originals[0];
    const last = this.originals[this.originals.length - 1];
    const gap = parseFloat(getComputedStyle(this.viewport).columnGap || getComputedStyle(this.viewport).gap) || 0;
    return last.offsetLeft + last.offsetWidth + gap - first.offsetLeft;
  }

  syncMode() {
    if (!this.looping) return;
    if (!this.isMobile()) {
      this._skipNormalize = true;
      this.viewport.scrollLeft = 0;
      requestAnimationFrame(() => {
        this._skipNormalize = false;
      });
      return;
    }
    this.jumpToMiddle();
  }

  jumpToMiddle() {
    if (!this.looping || !this.isMobile()) return;
    const width = this.setWidth();
    if (!width) return;
    this._skipNormalize = true;
    this.viewport.scrollLeft = width;
    requestAnimationFrame(() => {
      this._skipNormalize = false;
    });
  }

  normalizeLoop() {
    if (!this.looping || this._skipNormalize || !this.isMobile()) return;
    const width = this.setWidth();
    if (!width) return;

    const left = this.viewport.scrollLeft;
    if (left >= width * 2 - 4) {
      this._skipNormalize = true;
      this.viewport.scrollLeft = left - width;
      this._skipNormalize = false;
    } else if (left <= 4) {
      this._skipNormalize = true;
      this.viewport.scrollLeft = left + width;
      this._skipNormalize = false;
    }
  }

  canScroll() {
    return this.isMobile() && this.viewport.scrollWidth > this.viewport.clientWidth + 2;
  }

  onPointerDown(event) {
    if (!this.canScroll()) return;
    if (event.pointerType !== 'mouse' || event.button !== 0) return;

    this.dragging = true;
    this.dragMoved = false;
    this.startX = event.clientX;
    this.startScroll = this.viewport.scrollLeft;
    this.pointerId = event.pointerId;
    this.viewport.classList.add('is-grabbing');
  }

  onPointerMove(event) {
    if (!this.dragging || event.pointerId !== this.pointerId) return;

    const delta = event.clientX - this.startX;
    if (Math.abs(delta) <= 6) return;

    if (!this.dragMoved) {
      this.dragMoved = true;
      this.viewport.classList.add('is-dragging');
      try {
        this.viewport.setPointerCapture?.(event.pointerId);
      } catch (_) {
        /* ignore */
      }
    }

    this.viewport.scrollLeft = this.startScroll - delta;
    event.preventDefault();
  }

  onPointerUp(event) {
    if (!this.dragging || (this.pointerId != null && event.pointerId !== this.pointerId)) return;

    const didDrag = this.dragMoved;
    this.dragging = false;
    this.pointerId = null;
    this.viewport.classList.remove('is-dragging', 'is-grabbing');

    try {
      this.viewport.releasePointerCapture?.(event.pointerId);
    } catch (_) {
      /* ignore */
    }

    if (!didDrag) {
      this.dragMoved = false;
      return;
    }

    window.setTimeout(() => {
      this.dragMoved = false;
    }, 0);
  }
}

if (!customElements.get('adawat-deal-banners')) {
  customElements.define('adawat-deal-banners', AdawatDealBanners);
}
